/** Ethiopian calendar epoch (JDN). */
const ETHIOPIAN_EPOCH = 1723856

/**
 * English month labels mapped to Ethiopian months by common business usage.
 * Example: September -> Meskerem (month 1) -> Gregorian Sep 11 to Oct 10.
 */
export const ETHIOPIAN_MONTH_OPTIONS = [
  { value: '1', label: 'January', ethiopianMonth: 5, amharicName: 'ጥር' },
  { value: '2', label: 'February', ethiopianMonth: 6, amharicName: 'የካቲት' },
  { value: '3', label: 'March', ethiopianMonth: 7, amharicName: 'መጋቢት' },
  { value: '4', label: 'April', ethiopianMonth: 8, amharicName: 'ሚያዝያ' },
  { value: '5', label: 'May', ethiopianMonth: 9, amharicName: 'ግንቦት' },
  { value: '6', label: 'June', ethiopianMonth: 10, amharicName: 'ሰኔ' },
  { value: '7', label: 'July', ethiopianMonth: 11, amharicName: 'ሐምሌ' },
  { value: '8', label: 'August', ethiopianMonth: 12, amharicName: 'ነሐሴ' },
  { value: '9', label: 'September', ethiopianMonth: 1, amharicName: 'መስከረም' },
  { value: '10', label: 'October', ethiopianMonth: 2, amharicName: 'ጥቅምት' },
  { value: '11', label: 'November', ethiopianMonth: 3, amharicName: 'ኅዳር' },
  { value: '12', label: 'December', ethiopianMonth: 4, amharicName: 'ታኅሣሥ' }
]

/** Ethiopian months used for payroll are always 30 days. */
export const ETHIOPIAN_MONTH_DAYS = 30

function gregorianToJDN(year, month, day) {
  const a = Math.floor((14 - month) / 12)
  const y = year + 4800 - a
  const m = month + 12 * a - 3
  return (
    day +
    Math.floor((153 * m + 2) / 5) +
    365 * y +
    Math.floor(y / 4) -
    Math.floor(y / 100) +
    Math.floor(y / 400) -
    32045
  )
}

function jdnToGregorian(jdn) {
  let r = jdn + 68569
  const n = Math.floor((4 * r) / 146097)
  r -= Math.floor((146097 * n + 3) / 4)
  const y = Math.floor((4000 * (r + 1)) / 1461001)
  r = r - Math.floor((1461 * y) / 4) + 31
  const m = Math.floor((80 * r) / 2447)
  const d = r - Math.floor((2447 * m) / 80)
  r = Math.floor(m / 11)
  const month = m + 2 - 12 * r
  const year = 100 * (n - 49) + y + r
  return { year, month, day: d }
}

function ethiopianToJDN(year, month, day) {
  return ETHIOPIAN_EPOCH - 1 + 365 * (year - 1) + Math.floor((year - 1) / 4) + 30 * (month - 1) + day
}

function ethiopianToGregorian(year, month, day) {
  return jdnToGregorian(ethiopianToJDN(year, month, day))
}

function gregorianToEthiopian(year, month, day) {
  const jdn = gregorianToJDN(year, month, day)
  const r = jdn - ETHIOPIAN_EPOCH
  const ethYear = Math.floor((4 * r + 3) / 1461)
  const ethMonth =
    Math.floor((r - (365 * (ethYear - 1) + Math.floor((ethYear - 1) / 4))) / 30) + 1
  const ethDay =
    r - (365 * (ethYear - 1) + Math.floor((ethYear - 1) / 4) + 30 * (ethMonth - 1)) + 1
  return { year: ethYear, month: ethMonth, day: ethDay }
}

function toISODate({ year, month, day }) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/**
 * Format as DD-MM-YYYY (e.g. 11-09-2026).
 */
export function formatEthiopianRangeDate(isoDate) {
  if (!isoDate) return ''
  const match = String(isoDate).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return String(isoDate)
  return `${match[3]}-${match[2]}-${match[1]}`
}

export function getMonthOptionByValue(monthValue) {
  return ETHIOPIAN_MONTH_OPTIONS.find((month) => month.value === String(monthValue)) || null
}

export function formatMonthOptionWithAmharic(monthValueOrOption) {
  const option =
    typeof monthValueOrOption === 'object' && monthValueOrOption
      ? monthValueOrOption
      : getMonthOptionByValue(monthValueOrOption)
  if (!option) return String(monthValueOrOption || '')
  return `${option.label} | ${option.amharicName}`
}

/**
 * Resolve the Ethiopian year for a selected English month relative to today.
 * Future months in the current Gregorian year use the previous year's period
 * (e.g. September when today is July 2026 -> Sep 2025).
 */
export function resolveEthiopianYearForSelectedMonth(monthValue, referenceDate = new Date()) {
  const option = getMonthOptionByValue(monthValue)
  if (!option) return null

  const currentGregorianMonth = referenceDate.getMonth() + 1
  const selectedGregorianMonth = parseInt(String(monthValue), 10)
  let targetGregorianYear = referenceDate.getFullYear()
  if (selectedGregorianMonth > currentGregorianMonth) {
    targetGregorianYear -= 1
  }

  const baseEthYear = gregorianToEthiopian(
    referenceDate.getFullYear(),
    referenceDate.getMonth() + 1,
    referenceDate.getDate()
  ).year

  let bestEthYear = baseEthYear
  let bestScore = -1

  for (let ethYear = baseEthYear - 1; ethYear <= baseEthYear + 2; ethYear++) {
    const fromGregorian = ethiopianToGregorian(ethYear, option.ethiopianMonth, 1)
    const toGregorian = ethiopianToGregorian(ethYear, option.ethiopianMonth, 30)
    let score = 0
    if (fromGregorian.year === targetGregorianYear) score += 2
    if (toGregorian.year === targetGregorianYear) score += 1
    if (score > bestScore) {
      bestScore = score
      bestEthYear = ethYear
    }
  }

  return bestEthYear
}

/**
 * Gregorian date range for Ethiopian day 1-30 of the mapped month.
 */
export function getGregorianRangeForSelectedMonth(monthValue, referenceDate = new Date()) {
  const option = getMonthOptionByValue(monthValue)
  if (!option) return null

  const ethYear = resolveEthiopianYearForSelectedMonth(monthValue, referenceDate)
  if (ethYear == null) return null

  const fromGregorian = ethiopianToGregorian(ethYear, option.ethiopianMonth, 1)
  const toGregorian = ethiopianToGregorian(ethYear, option.ethiopianMonth, 30)

  return {
    option,
    ethYear,
    fromDate: toISODate(fromGregorian),
    toDate: toISODate(toGregorian),
    fromDisplay: formatEthiopianRangeDate(toISODate(fromGregorian)),
    toDisplay: formatEthiopianRangeDate(toISODate(toGregorian))
  }
}
